import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Import util modules
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';

// Import shared components
import { Modal, Button, IconSpinner } from '../../components';

// Import modules from this directory
import css from './LinkedProviderModal.module.css';

const formatServiceLabel = service => {
  if (!service) return '';
  const words = service
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .trim()
    .split(/\s+/);
  return words
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * LinkedProviderModal component displays a modal with service provider listings
 * that match the selected services needed for a tour.
 */
const LinkedProviderModal = props => {
  const {
    id,
    isOpen,
    onClose,
    onManageDisableScrolling,
    providers,
    providersIncluded,
    onSelectProvider,
    selectedProviderId,
    isLoading,
    error,
  } = props;

  const intl = useIntl();
  const [localSelectedId, setLocalSelectedId] = useState(selectedProviderId);

  const handleSelect = providerId => {
    setLocalSelectedId(providerId);
  };

  const handleConfirm = () => {
    if (localSelectedId) {
      const selectedProvider = providers.find(p => p.id.uuid === localSelectedId);
      onSelectProvider(selectedProvider);
      onClose();
    }
  };

  return (
    <Modal
      id={id}
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      containerClassName={css.modalContainer}
      usePortal
    >
      <div className={css.root}>
        <h2 className={css.title}>
          <FormattedMessage id="LinkedProviderModal.title" />
        </h2>

        {isLoading ? (
          <div className={css.loadingContainer}>
            <IconSpinner />
            <p className={css.loadingText}>
              <FormattedMessage id="LinkedProviderModal.loading" />
            </p>
          </div>
        ) : error ? (
          <div className={css.error}>
            <p>
              <FormattedMessage id="LinkedProviderModal.error" />
            </p>
          </div>
        ) : providers.length === 0 ? (
          <div className={css.noResults}>
            <p>
              <FormattedMessage id="LinkedProviderModal.noProviders" />
            </p>
          </div>
        ) : (
          <div className={css.providersList}>
            {providers.map(provider => {
              const { id, attributes, relationships } = provider;
              const { title, description, price, publicData } = attributes;
              const images = provider.images || [];
              const firstImage = images.length > 0 ? images[0] : null;
              const isSelected = localSelectedId === id.uuid;

              // Get author (provider) information from included resources
              const authorId = relationships?.author?.data?.id?.uuid;
              const author = authorId && providersIncluded 
                ? providersIncluded.find(item => item.type === 'user' && item.id.uuid === authorId)
                : null;
              
              const providerName = author?.attributes?.profile?.displayName || 
                                   author?.attributes?.profile?.abbreviatedName || 
                                   'Provider';
              
              // Get profile image from included resources
              const profileImageId = author?.relationships?.profileImage?.data?.id?.uuid;
              const profileImage = profileImageId && providersIncluded
                ? providersIncluded.find(item => item.type === 'image' && item.id.uuid === profileImageId)
                : null;

              return (
                <div
                  key={id.uuid}
                  className={classNames(css.providerCard, {
                    [css.selected]: isSelected,
                  })}
                  onClick={() => handleSelect(id.uuid)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleSelect(id.uuid);
                    }
                  }}
                >
                  {firstImage ? (
                    <div className={css.imageContainer}>
                      <img
                        src={firstImage.attributes.variants['listing-card']?.url || firstImage.attributes.variants['default']?.url}
                        alt={title}
                        className={css.image}
                      />
                    </div>
                  ) : (
                    <div className={css.imageContainer}>
                      <div className={css.noImage}>
                        <span className={css.noImageText}>
                          {title.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className={css.providerInfo}>
                    <h3 className={css.providerTitle}>{title}</h3>
                    <div className={css.providerMeta}>
                      {profileImage && (
                        <img
                          src={profileImage.attributes?.variants?.square?.url || profileImage.attributes?.variants?.default?.url}
                          alt={providerName}
                          className={css.providerAvatar}
                        />
                      )}
                      <span className={css.providerName}>by {providerName}</span>
                    </div>
                    {description && (
                      <p className={css.providerDescription}>
                        {description.length > 150
                          ? `${description.substring(0, 150)}...`
                          : description}
                      </p>
                    )}
                    {price && (
                      <p className={css.providerPrice}>
                        {formatMoney(intl, price)}
                        {publicData?.unitType && (
                          <span className={css.unitType}>
                            {' '}
                            / <FormattedMessage id={`LinkedProviderModal.unitType.${publicData.unitType}`} />
                          </span>
                        )}
                      </p>
                    )}
                    {publicData?.service_type && (
                      <p className={css.serviceType}>
                        <FormattedMessage
                          id={`LinkedProviderModal.serviceType.${publicData.service_type.toLowerCase().trim()}`}
                          defaultMessage={formatServiceLabel(publicData.service_type)}
                        />
                      </p>
                    )}
                  </div>
                  {isSelected && (
                    <div className={css.selectedIndicator}>
                      <span className={css.checkmark}>✓</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className={css.actions}>
          <Button rootClassName={css.cancelButton} onClick={onClose}>
            <FormattedMessage id="LinkedProviderModal.cancel" />
          </Button>
          <Button
            rootClassName={css.confirmButton}
            onClick={handleConfirm}
            disabled={!localSelectedId || isLoading}
          >
            <FormattedMessage id="LinkedProviderModal.confirm" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};

LinkedProviderModal.defaultProps = {
  providers: [],
  providersIncluded: [],
  selectedProviderId: null,
  error: null,
};

LinkedProviderModal.propTypes = {
  id: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  providers: PropTypes.array,
  providersIncluded: PropTypes.array,
  onSelectProvider: PropTypes.func.isRequired,
  selectedProviderId: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.object,
};

export default LinkedProviderModal;
